import React, { useMemo, useState } from 'react';
import { HelpCircle } from 'react-feather';
import { Grid, Typography, makeStyles, ButtonBase } from '@material-ui/core';
import { RadioButtonGroup } from '../../Inputs';
import { FormValueTypes } from '../../../types';
import { FormHookResult } from '../../../validation';
import colors from '../../../theme/colors';

import DefaultCard from '../../General/DefaultCard';
import ErrorLabel from '../../General/ErrorLabel';
import InstructionsLayout from '../InstructionsLayout';

import booleanRadioData from '../../../constants/booleanRadioData';

const useStyles = makeStyles({
  disabledText: {
    color: colors.silver,
  },
  container: {
    position: 'relative',
  },
  helpCircle: {
    position: 'absolute',
    right: 4,
    top: -28,
  },
  instructionL: {
    padding: '1em 2.5em',
  },
});

function getMappedBooleanValue(value: FormValueTypes): string | undefined {
  if (typeof value !== 'undefined') {
    return value ? 'yes' : 'no';
  }
  return value;
}

type MakeLayoutProps = {
  sharedDisabledValues: string[];
  getInputProps: FormHookResult;
  isDisabled: boolean;
};
function makeLayoutForNaturalPerson({
  sharedDisabledValues,
  getInputProps,
  isDisabled,
}: MakeLayoutProps): React.ReactNode {
  const usedPepValue = getMappedBooleanValue(getInputProps('pep').value);
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="caption" data-testid="natural-person-pep-label">
          ¿Desempeñas actualmente o has desempeñado un puesto político o con
          influencia política en los últimos dos años?
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <RadioButtonGroup
          inputId="sales-web-inspection-radio-ispep"
          radioGroupName="pep"
          formTitle=""
          defaultValue={usedPepValue}
          radioData={booleanRadioData}
          onChange={(newVal): void => {
            getInputProps('pep').onChange(newVal.Value === 'yes');
          }}
          centerLabels
          disabledValues={sharedDisabledValues}
          disabled={isDisabled}
          errorMessage={getInputProps('pep').errorText}
        />
      </Grid>
    </>
  );
}

function makeLayoutForLegalPerson({
  sharedDisabledValues,
  getInputProps,
  isDisabled,
}: MakeLayoutProps): React.ReactNode {
  const usedPepValue = getMappedBooleanValue(getInputProps('pep').value);
  const usedPepBloodRelated = getMappedBooleanValue(
    getInputProps('bloodRelatedToPep').value,
  );
  const mustShowError = usedPepValue === 'yes' || usedPepBloodRelated === 'yes';

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="caption" data-testid="legal-person-pep-label">
          ¿Alguno de los accionistas desempeña actualmente o ha desempeñado un
          puesto político o con influencia política en los últimos dos años?
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <RadioButtonGroup
          inputId="sales-web-inspection-radio-ispep"
          radioGroupName="pep"
          formTitle=""
          defaultValue={usedPepValue}
          radioData={booleanRadioData}
          onChange={(newVal): void => {
            getInputProps('pep').onChange(newVal.Value === 'yes');
          }}
          disabledValues={sharedDisabledValues}
          disabled={isDisabled}
          errorMessage={getInputProps('pep').errorText}
          centerLabels
        />
      </Grid>
      {usedPepValue === 'no' && typeof usedPepValue !== 'undefined' && (
        <>
          <Grid item xs={12}>
            <Typography variant="caption">
              ¿Alguno de los accionistas mantiene un grado de consanguinidad o
              afinidad con un PEP (Persona Políticamente Expuesta)?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <RadioButtonGroup
              inputId="sales-web-inspection-radio-ispepbloodrelated"
              radioGroupName="bloodRelatedToPep"
              formTitle=""
              defaultValue={usedPepBloodRelated}
              radioData={booleanRadioData}
              onChange={(newVal): void => {
                getInputProps('bloodRelatedToPep').onChange(
                  newVal.Value === 'yes',
                );
              }}
              disabled={isDisabled}
              errorMessage={getInputProps('bloodRelatedToPep').errorText}
              centerLabels
            />
          </Grid>
        </>
      )}
      {mustShowError && (
        <Grid item xs={12}>
          <ErrorLabel data-testid="legal-person-error">
            La emisión (suscripción de póliza) deberá realizarse en nuestras
            oficinas. Por favor contacta a tu Corredor o a un Ejecutivo
            Comercial al 800-ASSA (2772) para recibir asistencia.
          </ErrorLabel>
        </Grid>
      )}
    </>
  );
}

type Props = {
  /**  A helper method that lets you extract the required data for an specific input name */
  getInputProps: FormHookResult;
  /** Use when component needs to be disabled */
  isDisabled: boolean;
  /** Set "true" for legal person layout */
  useLegalEntityLayout?: boolean;
  cardTitle?: string;
};

/**
 * A card that displays required inputs for the "pep" field. Note that
 * it uses a radiogroup to display a boolean element, so an additional
 * mapping is required.
 */
const PepCard = ({
  getInputProps,
  isDisabled,
  useLegalEntityLayout = false,
  cardTitle = 'Persona Expuesta Políticamente',
}: Props): JSX.Element => {
  const classes = useStyles();
  const sharedDisabledValues = useMemo(
    () => (isDisabled ? ['yes', 'no'] : ['']),
    [isDisabled],
  );

  const [instructionVisible, toggleInstructionVisible] = useState<boolean>(
    false,
  );

  return (
    <>
      <InstructionsLayout
        id="pep-help-modal"
        data-testid="instruction-modal"
        open={instructionVisible}
        headerTitle="Persona Expuesta Políticamente"
        title=""
        content={
          <Typography
            variant="h4"
            align="center"
            className={classes.instructionL}
          >
            Se considera PEP a quien en los últimos dos años desempeñó o ha
            desempeñado funciones públicas destacadas a nivel nacional o en el
            extranjero (ej.: jefe de estado o gobierno, políticos, funcionarios
            gubernamentales, judiciales o militares de alta jerarquía, altos
            ejecutivos de empresas estatales, funcionarios importantes de
            partidos políticos). O quienes mantengan primer grado de
            consanguinidad o afinidad (Padres, Hermanos, Hijos, Cónyugue) con el
            PEP.
          </Typography>
        }
        instructions=""
        onCloseRequested={(): void => toggleInstructionVisible(false)}
      />
      <DefaultCard disabled={isDisabled} title={cardTitle} data-testid="card">
        <Grid item container alignItems="flex-end" justifyContent="flex-end">
          <ButtonBase
            disableRipple
            data-testid="id-instrunctions-button"
            style={{ display: 'inline-block' }}
            onClick={(): void => toggleInstructionVisible(true)}
          >
            <HelpCircle
              className={classes.helpCircle}
              size={24}
              color={colors.bright_turquoise}
            />
          </ButtonBase>
        </Grid>
        <Grid container spacing={2} className={classes.container}>
          {useLegalEntityLayout
            ? makeLayoutForLegalPerson({
                sharedDisabledValues,
                getInputProps,
                isDisabled,
              })
            : makeLayoutForNaturalPerson({
                sharedDisabledValues,
                getInputProps,
                isDisabled,
              })}
        </Grid>
      </DefaultCard>
    </>
  );
};

export default PepCard;
